/*
  ml-cart-drawer-v2 — the drawer shell for sections/ml-cart-drawer-v2 (Pass 7 Cart board D).

  Keeps the contract of assets/monthlees-cart-drawer.js, which this replaces when the theme
  setting is on, so nothing that opens the cart has to change:
  - [data-monthlees-cart-trigger] links open the drawer when data-cart-drawer-enabled="true";
  - product forms (form.monthlees-product-form / .monthlees-product-card__form posting to
    /cart/add) are added by ajax and then open the drawer, with errors shown in the form's
    [data-monthlees-form-error] slot;
  - the cart count badges are kept current;
  - window.MonthleesCartDrawer = { refresh, open, show, close }.

  Board behaviour: opens on add to cart with focus on the heading; Esc, the backdrop and x
  close it and focus returns to what opened it; Tab stays inside while it is open. Content
  and every figure come from assets/ml-cart-v2.js (Section Rendering API).
*/
(function () {
  'use strict';

  var drawer = document.querySelector('[data-ml-cart-drawer]');
  if (!drawer || drawer.getAttribute('data-ready') === 'true') return;
  drawer.setAttribute('data-ready', 'true');

  var panel = drawer.querySelector('[data-ml-cart-panel]');
  var TRIGGER = '[data-monthlees-cart-trigger]';
  var PRODUCT_FORM =
    'form.monthlees-product-form[action*="/cart/add"], form.monthlees-product-card__form[action*="/cart/add"]';
  var FORM_ERROR = '[data-monthlees-form-error]';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lastFocus = null;
  var closeTimer = null;

  function cart() {
    return window.MlCartV2;
  }

  function refresh() {
    var c = cart();
    if (c && typeof c.render === 'function') return c.render();
    return Promise.resolve();
  }

  function focusTitle() {
    var title = drawer.querySelector('#MlCartDrawerTitle');
    if (title) title.focus({ preventScroll: true });
  }

  function reveal() {
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (drawer.hidden) lastFocus = document.activeElement;
    drawer.hidden = false;
    document.documentElement.classList.add('ml-cart-drawer-lock', 'monthlees-cart-drawer-open');
    // A forced reflow, not requestAnimationFrame: rAF is paused in a background tab, which
    // would leave the drawer revealed but still off-screen.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    focusTitle();
  }

  function open(options) {
    var skip = options && options.skipFetch;
    reveal();
    if (!skip) return refresh().then(focusTitle, function () {});
    return Promise.resolve();
  }

  function close() {
    if (drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('ml-cart-drawer-lock', 'monthlees-cart-drawer-open');
    var done = function () {
      drawer.hidden = true;
      closeTimer = null;
      if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) {
        lastFocus.focus({ preventScroll: true });
      }
    };
    if (reduced) done();
    else closeTimer = window.setTimeout(done, 260);
  }

  // Close: backdrop and x (delegated; the x is re-rendered with the content).
  drawer.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-ml-cart-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (drawer.hidden) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab' || !panel) return;
    var items = Array.prototype.slice
      .call(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(function (el) { return el.offsetParent !== null || el.getClientRects().length; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  });

  // Header cart links.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest(TRIGGER);
    if (!trigger) return;
    if (trigger.getAttribute('data-cart-drawer-enabled') !== 'true') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    open();
  });

  // Ajax add for product forms, as the existing drawer does.
  function buildFormData(form) {
    var data = new FormData(form);
    var card = form.closest('.monthlees-pdp__purchase-card');
    if (card) {
      card
        .querySelectorAll('input[name="selling_plan"], select[name="selling_plan"], input[name^="properties"], select[name^="properties"]')
        .forEach(function (el) {
          if (!el.name || !el.value || form.contains(el)) return;
          if (el.type === 'radio' && !el.checked) return;
          data.set(el.name, el.value);
        });
    }
    return data;
  }

  function formError(form, message) {
    var el = form.querySelector(FORM_ERROR);
    if (!el) {
      window.alert(message);
      return;
    }
    el.textContent = message;
    el.hidden = false;
    el.focus && el.focus();
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(PRODUCT_FORM)) return;
    var submitter = e.submitter;
    if (submitter && submitter.name && submitter.name !== 'add') return;
    e.preventDefault();

    var btn = form.querySelector('[name="add"]');
    if (btn) btn.disabled = true;
    var err = form.querySelector(FORM_ERROR);
    if (err) {
      err.textContent = '';
      err.hidden = true;
    }

    fetch('/cart/add.js', { method: 'POST', body: buildFormData(form), credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () {
        lastFocus = btn || document.activeElement;
        return refresh();
      })
      .then(function () {
        open({ skipFetch: true });
        document.dispatchEvent(new CustomEvent('monthlees:cart-updated', { detail: { source: 'ml-cart-v2' } }));
      })
      .catch(function (error) {
        formError(form, (error && (error.description || error.message)) || 'Could not add to cart. Please try again.');
      })
      .finally(function () {
        if (btn) btn.disabled = false;
      });
  });

  function syncBadges() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (c) {
        if (cart()) cart().updateBadges(c.item_count);
      })
      .catch(function () {});
  }
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      syncBadges();
      if (!drawer.hidden) refresh();
    }
  });
  syncBadges();

  window.MonthleesCartDrawer = {
    refresh: function () { return refresh(); },
    open: open,
    show: function () { return open({ skipFetch: true }); },
    close: close
  };
})();
